/**
 * DEC5 targets for /overview boxes 3 and 9, judged on the client (spec §4):
 * a line-for-line port of api/_lib/lookerTargets.ts cellOf / resolveFor /
 * headerPlatform (ruling R7), so /overview and /looker give the same status
 * for the same inputs — the parity test feeds both the same rows. A failed
 * read is 'failed' (never 'none'); no page currency is 'noCurrency'; a target
 * kept for another Result definition is 'otherMetric' and never judged.
 * Statuses come from targetVerdict only, never from a rounded number. Pure.
 *
 * @module services/overview/overviewTargets
 */
import {
  resolveCprTarget, targetChangeWithin, targetVerdict, TARGET_PLATFORMS,
  type CprTargetRow, type TargetObjective, type TargetPlatform,
} from '@/services/targets/targetRules';
import { ALLOWED_METRICS, metricFor, type AwarenessMetric, type ResultMetric } from '@/services/targets/resultMetric';
import type { TileTargetCell, TileTargets } from '@/services/looker/lookerTypes';

export interface OverviewTargetContext {
  /** The workspace's target rows (only 'approved' are ever used); 'failed' = the read failed. */
  rows: readonly CprTargetRow[] | 'failed';
  /** DEC3: the Awareness definition in force; null = unread → every cell 'failed'. */
  awareness: AwarenessMetric | null;
  /** The page currency; null = none known → 'noCurrency'. */
  currency: string | null;
  /** The page's channel ids; [] = every channel. */
  channels: readonly string[];
  /** The period's first day: `changed` looks inside (periodStart, asOf]. */
  periodStart: string;
  /** windows.through: Cost per Result is computed through it, so the target is looked up on it. */
  asOf: string;
}

const onlyPlatform = (channels: readonly string[]): TargetPlatform | null =>
  (channels.length === 1 && (TARGET_PLATFORMS as readonly string[]).includes(channels[0]) ? (channels[0] as TargetPlatform) : null);

const emptyCell = (objective: TargetObjective, status: 'failed' | 'noCurrency'): TileTargetCell =>
  ({ objective, target: null, status, deltaPct: null, changed: null, platform: null });

interface Found { row: CprTargetRow | null; platform: TargetPlatform | null }

/** I1: the filtered channel's own target for this objective and metric, else the objective-level one. */
const resolveFor = (
  rows: readonly CprTargetRow[], objective: TargetObjective, metric: ResultMetric, currency: string, channel: TargetPlatform | null, asOf: string,
): Found => {
  const channelRow = channel ? resolveCprTarget(rows, { objective, metric, currency, platform: channel, date: asOf }) : null;
  return channelRow
    ? { row: channelRow, platform: channel }
    : { row: resolveCprTarget(rows, { objective, metric, currency, platform: null, date: asOf }), platform: null };
};

export const targetCellFor = (objective: TargetObjective, cpr: number | null, ctx: OverviewTargetContext): TileTargetCell => {
  const { rows, currency, awareness } = ctx;
  if (rows === 'failed' || awareness === null) return emptyCell(objective, 'failed');
  if (currency === null) return emptyCell(objective, 'noCurrency');
  const channel = onlyPlatform(ctx.channels);
  const metric = metricFor(objective, awareness);
  const found = resolveFor(rows, objective, metric, currency, channel, ctx.asOf);
  if (!found.row) {
    // A target kept for another definition is named, never judged, and never reported as "not set".
    const other = ALLOWED_METRICS[objective].filter((m) => m !== metric)
      .map((m) => ({ m, hit: resolveFor(rows, objective, m, currency, channel, ctx.asOf) }))
      .find((x) => x.hit.row !== null);
    if (other) {
      return { objective, target: null, status: 'otherMetric', deltaPct: null, changed: null, platform: other.hit.platform, setAgainst: other.m };
    }
  }
  const key = { objective, metric, currency, platform: found.platform };
  const target = found.row?.target ?? null;
  const verdict = targetVerdict(cpr, target);
  const changed = targetChangeWithin(rows, { ...key, start: ctx.periodStart, end: ctx.asOf });
  return { objective, target, status: verdict.status, deltaPct: verdict.deltaPct, changed, platform: found.platform };
};

/** The header names a channel only when every cell that has a target used that channel's own. */
const headerPlatform = (cells: readonly TileTargetCell[]): TargetPlatform | null => {
  const judged = cells.filter((c) => c.target !== null);
  const first = judged[0]?.platform ?? null;
  return first !== null && judged.every((c) => c.platform === first) ? first : null;
};

export interface ObjectiveCpr { objective: TargetObjective; cpr: number | null }

/** One cell per objective; null when there is none or an objective repeats (never a verdict on an ambiguous key). */
export const overviewTargetsOf = (pairs: readonly ObjectiveCpr[], ctx: OverviewTargetContext): TileTargets | null => {
  if (pairs.length === 0 || new Set(pairs.map((p) => p.objective)).size !== pairs.length) return null;
  const cells = pairs.map((p) => targetCellFor(p.objective, p.cpr, ctx));
  return {
    asOf: ctx.asOf,
    currency: ctx.currency,
    platform: headerPlatform(cells),
    failed: ctx.rows === 'failed' || ctx.awareness === null,
    byObjective: Object.fromEntries(cells.map((c) => [c.objective, c])),
  };
};
